<template>
  <p>{{ name }}</p>

  <div class="project-list-wrapper" :class="{ 'no-projects': projectItems.length === 0 }">
    <div v-if="isError" class="error-container">
      <BaseIcon iconName="alert" color="#ff6b6b" />
      <span>{{ $t('projects.failedToLoad') }}</span>
      <SecondBtn @click="$emit('retry')">{{ $t('common.retry') }}</SecondBtn>
    </div>
    <div v-else-if="isLoading"
      :class="{ 'skeleton-grid': true, 'single-row': isSingleRow, 'multi-row': !isSingleRow }">
      <div v-for="n in (isSingleRow ? 4 : 6)" :key="n" class="skeleton-card">
        <div class="skeleton-image"></div>
        <div class="skeleton-text skeleton-text-name"></div>
        <div class="skeleton-text skeleton-text-date"></div>
      </div>
    </div>
    <div v-else-if="projectItems.length > 0"
      :class="{ 'project-list': true, 'single-row': isSingleRow, 'multi-row': !isSingleRow }">
      <ProjectItem v-for="(item, index) in sortedProjectItems" :key="index" :imgUrl="item.imgUrl"
        :imgName="item.user_animation_name" :imgDate="item.date" :id="String(item.hashed_id)"
        :isFavorite="item.isFavorite" :isShowFavoriteStar="isFavoriteInline" :viewed="item.viewed"
        :animationStatus="item.animation_status" />
    </div>

    <div v-else class="placeholder">
      <BaseIcon :iconName="iconName" color="rgba(255,255,255, .4)"></BaseIcon>
      <p class="caption">{{ captionText }}</p>
      <SecondBtn
        v-if="emptyCtaText && emptyCtaRoute"
        size="md"
        class="empty-cta"
        @click="$router.push(emptyCtaRoute)"
      >
        {{ emptyCtaText }}
      </SecondBtn>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import ProjectItem from '@/components/ProjectItem.vue';
import BaseIcon from '@/components/BaseComponents/BaseIcon.vue';
import SecondBtn from '@/components/BaseComponents/SecondBtn.vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const props = defineProps({
  name: String,
  projectItems: {
    type: Array,
    default: () => [],
  },
  isSingleRow: {
    type: Boolean,
    default: true,
  },
  locked: {
    type: Boolean,
    default: false,
  },
  isFavoriteInline: {
    type: Boolean,
    default: true,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  error: {
    type: Boolean,
    default: false
  },
  emptyCtaText: {
    type: String,
    default: '',
  },
  emptyCtaRoute: {
    type: String,
    default: '',
  },
});

const isLoading = ref(!props.locked);
const isError = ref(false);
let loadingStartedAt = isLoading.value ? Date.now() : 0;
const MIN_LOADING_MS = 500;

defineEmits(['retry']);

watch(() => props.loading, (newValue) => {
  if (newValue) {
    loadingStartedAt = Date.now();
    isLoading.value = true;
  } else {
    const elapsed = Date.now() - loadingStartedAt;
    const remaining = Math.max(0, MIN_LOADING_MS - elapsed);
    setTimeout(() => {
      isLoading.value = false;
    }, remaining);
  }
});

watch(() => props.error, (newValue) => {
  isError.value = newValue;
});

const sortedProjectItems = computed(() => {
  return props.projectItems.slice().sort((a, b) => new Date(b.date) - new Date(a.date));
});

const iconName = computed(() => (props.locked ? 'lock' : 'dotHorizontal'));

const captionText = computed(() => {
  if (props.locked) {
    return t('projects.unavailable');
  } else {
    return t('projects.empty', { name: props.name.toLowerCase() });
  }
});
</script>

<style scoped>
.project-list {
  display: flex;
  flex-direction: row;
  width: 100%;
  padding-bottom: var(--spacing-sm-lg);
}

.placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  color: var(--color-gray);
  font-size: var(--font-size-base);
}

.no-projects {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: var(--spacing-3xl) var(--spacing-xl);
  box-sizing: border-box;
  width: 100%;
  border-radius: var(--border-radius-md);
  border: 1px solid var(--color-white-10);
  color: var(--color-white-40);
}

.caption {
  color: var(--color-white-40);
}

.empty-cta {
  margin-top: var(--spacing-lg);
}

.single-row {
  display: flex;
  flex-wrap: nowrap;
  overflow-x: auto;
  gap: var(--spacing-xl);
  padding: var(--spacing-xs) var(--spacing-xs) var(--spacing-md) var(--spacing-xs);
  align-items: flex-start;
}

.single-row > * {
  flex: 0 0 auto;
  width: 250px;
}

.multi-row {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: var(--spacing-xl);
  width: 100%;
  max-height: calc(100vh - 200px);
  overflow-y: auto;
  padding-right: var(--spacing-xs);
}

@media (max-width: 1200px) {
  .multi-row {
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: var(--spacing-lg);
  }
}

@media (max-width: 768px) {
  .multi-row {
    grid-template-columns: repeat(2, 1fr);
    gap: var(--spacing-md);
  }

  .single-row {
    gap: var(--spacing-md);
    padding: var(--spacing-xs) var(--spacing-xs) var(--spacing-md) var(--spacing-xs);
  }

  .single-row > * {
    width: 180px;
  }
}

@media (max-width: 480px) {
  .multi-row {
    grid-template-columns: 1fr;
    gap: var(--spacing-lg);
  }

  .single-row > * {
    width: 160px;
  }
}

.skeleton-grid {
  width: 100%;
}

.skeleton-card {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

.skeleton-image {
  width: 100%;
  aspect-ratio: 4 / 3;
  border-radius: var(--border-radius-md);
  background: var(--color-white-10);
  background-image: linear-gradient(
    90deg,
    var(--color-white-10) 0%,
    var(--color-white-15) 50%,
    var(--color-white-10) 100%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite linear;
}

.skeleton-text {
  height: 12px;
  border-radius: var(--border-radius-xs);
  background: var(--color-white-10);
  background-image: linear-gradient(
    90deg,
    var(--color-white-10) 0%,
    var(--color-white-15) 50%,
    var(--color-white-10) 100%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite linear;
}

.skeleton-text-name {
  width: 70%;
}

.skeleton-text-date {
  width: 40%;
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-sm-xs);
}

.error-container span {
  color: var(--color-white-70);
  font-size: var(--font-size-base);
}

.error-container :deep(.second-btn) {
  margin-top: var(--spacing-sm);
  min-width: 120px;
}
</style>
